import { useState, useRef } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { Download, Upload, X } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function QRGenerator() {
  const [text, setText] = useState('')
  const [size, setSize] = useState(300)
  const [errorLevel, setErrorLevel] = useState('M')
  const [fgColor, setFgColor] = useState('#000000')
  const [bgColor, setBgColor] = useState('#FFFFFF')
  const [logo, setLogo] = useState(null)
  const [logoSize, setLogoSize] = useState(60)
  const [marginSize, setMarginSize] = useState(4)
  const logoInputRef = useRef(null)

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0]
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader()
      reader.onload = (event) => {
        setLogo(event.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const removeLogo = () => {
    setLogo(null)
    if (logoInputRef.current) {
      logoInputRef.current.value = ''
    }
  }

  const downloadQR = () => {
    const svg = document.getElementById('qr-code-svg')
    if (!svg) return

    const svgData = new XMLSerializer().serializeToString(svg)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const img = new Image()

    img.onload = () => {
      canvas.width = size
      canvas.height = size
      ctx.drawImage(img, 0, 0)
      
      // Draw logo if present
      if (logo) {
        const logoImg = new Image()
        logoImg.onload = () => {
          const logoX = (size - logoSize) / 2
          const logoY = (size - logoSize) / 2
          ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize)
          
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = 'qrcode.png'
            link.click()
            URL.revokeObjectURL(url)
          })
        }
        logoImg.src = logo
      } else {
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = 'qrcode.png'
          link.click()
          URL.revokeObjectURL(url)
        })
      }
    }

    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)))
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">Enter text or URL:</label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="input w-full"
              placeholder="https://example.com or any text"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-700 font-semibold mb-3">Size:</label>
              <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="input w-full">
                <option value={200}>200x200 (Small)</option>
                <option value={300}>300x300 (Medium)</option>
                <option value={400}>400x400 (Large)</option>
                <option value={500}>500x500 (Extra Large)</option>
              </select>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Error Correction Level:</label>
              <select value={errorLevel} onChange={(e) => setErrorLevel(e.target.value)} className="input w-full">
                <option value="L">L (Low ~7%)</option>
                <option value="M">M (Medium ~15%)</option>
                <option value="Q">Q (Quartile ~25%)</option>
                <option value="H">H (High ~30%)</option>
              </select>
            </div>
          </div>

          {/* Customization Options */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize Appearance</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Foreground Color:</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="input flex-1"
                    placeholder="#000000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-3">Background Color:</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-16 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="input flex-1"
                    placeholder="#FFFFFF"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-gray-700 font-semibold mb-3">Margin Size:</label>
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={marginSize}
                  onChange={(e) => setMarginSize(Number(e.target.value))}
                  className="w-full"
                />
                <div className="text-sm text-gray-500 mt-1">{marginSize}px</div>
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-3">Logo Size:</label>
                <input
                  type="range"
                  min="30"
                  max={Math.min(size * 0.3, 120)}
                  value={logoSize}
                  onChange={(e) => setLogoSize(Number(e.target.value))}
                  className="w-full"
                  disabled={!logo}
                />
                <div className="text-sm text-gray-500 mt-1">{logoSize}px {!logo && '(Upload logo to enable)'}</div>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 font-semibold mb-3">Logo/Image (Optional):</label>
              <div className="flex items-center gap-3">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <Button
                  variant="outline"
                  onClick={() => logoInputRef.current?.click()}
                  type="button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {logo ? 'Change Logo' : 'Upload Logo'}
                </Button>
                {logo && (
                  <div className="flex items-center gap-2">
                    <img src={logo} alt="Logo preview" className="w-12 h-12 object-contain border border-gray-300 rounded" />
                    <button
                      onClick={removeLogo}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Remove logo"
                      type="button"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Add a logo or image in the center. Use high error correction (H) for best results with logos.
              </p>
            </div>
          </div>
        </Card.Body>
      </Card>

      {text && (
        <Card>
          <Card.Body>
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div id="qr-code-container" className="relative inline-block">
                  <QRCodeSVG
                    id="qr-code-svg"
                    value={text}
                    size={size}
                    level={errorLevel}
                    includeMargin={marginSize > 0}
                    fgColor={fgColor}
                    bgColor={bgColor}
                    imageSettings={
                      logo
                        ? {
                            src: logo,
                            height: logoSize,
                            width: logoSize,
                            excavate: true,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>
              <Button onClick={downloadQR} variant="primary" size="lg">
                <Download className="w-5 h-5 mr-2" />
                Download QR Code
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      {!text && (
        <Card>
          <Card.Body className="text-center py-12">
            <p className="text-gray-600">Enter text or URL above to generate QR code</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

